import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faSave,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '@/services/userService';

const AgentProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    centerAddress: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await userService.getMe();
        const me = data?.user;
        setUser(me);
        setFormData({
          name: me?.name || '',
          phone: me?.phone || '',
          centerAddress: me?.centerAddress || ''
        });
      } catch (err) {
        console.error('Failed to fetch profile', err);
        setError('Could not load profile information.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updated = await userService.updateMe(formData);
      setUser(updated);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      console.error('Update profile failed', err);
      setError(err?.response?.data?.msg || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-8 text-white">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold border-2 border-white/30">
              {formData.name ? formData.name.charAt(0).toUpperCase() : <FontAwesomeIcon icon={faUser} />}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{formData.name || 'Agent Profile'}</h2>
              <div className="flex flex-col gap-1 mt-1">
                <p className="text-white/80 flex items-center gap-2">
                  <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
                  {user?.email}
                </p>
                {user?.customId && (
                  <div className="inline-flex items-center w-fit bg-white/20 backdrop-blur-md px-3 py-0.5 rounded-full border border-white/30">
                    <span className="text-xs font-mono tracking-widest text-white">ID: {user.customId}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded flex items-center gap-3 text-red-700">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded flex items-center gap-3 text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} />
              <p className="text-sm font-medium">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FontAwesomeIcon icon={faUser} className="text-xs" />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="Your full name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FontAwesomeIcon icon={faPhone} className="text-xs" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="+234..."
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Center Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none text-gray-400">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
              </div>
              <textarea
                name="centerAddress"
                value={formData.centerAddress}
                onChange={handleChange}
                rows="3"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                placeholder="The physical address of your pickup center"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 italic">
              Updating this address will change the pickup location displayed to customers for your center.
            </p>
          </div>

          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className={`px-8 py-3 rounded-xl font-bold text-white flex items-center gap-2 transition-all shadow-lg ${
                saving ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 hover:shadow-red-500/30'
              }`}
            >
              <FontAwesomeIcon icon={saving ? faSave : faSave} className={saving ? 'animate-pulse' : ''} />
              {saving ? 'Saving Changes...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentProfile;